import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import CTASection from "../components/home/CTASection";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import useReveal from "../hooks/useReveal";
import { useEstimate } from "../components/EstimateContext";
import {
  TrendingUp, Package, Sun, LayoutGrid, Leaf, Sparkles, MessageSquare, PenTool, Layers,
  Hammer, CheckCircle2, Plus, Minus, ArrowRight, Check, Star, ZoomIn,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "../components/ui/carousel";
import {
  IMG, BENEFITS, PROCESS, STYLES, CABINET_MATERIALS, STORAGE, COUNTERTOPS, LIGHTING,
  LAYOUTS, DOOR_STYLES, HARDWARE, FLOORING, FEATURES, GALLERY, FAQ, RESOURCES,
} from "../data/kitchenData";

const ICONS = { TrendingUp, Package, Sun, LayoutGrid, Leaf, Sparkles, MessageSquare, PenTool, Layers, Hammer, CheckCircle2 };

const Heading = ({ eyebrow, title, sub, center }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} mb-12 reveal`}>
    <div className={`flex items-center gap-3 mb-4 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-[#2f9fe6]" />
      <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">{eyebrow}</span>
      {center && <span className="h-px w-10 bg-[#2f9fe6]" />}
    </div>
    <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight">{title}</h2>
    {sub && <p className="text-[#6b7683] text-lg mt-4">{sub}</p>}
  </div>
);

export default function KitchenRenovations() {
  useReveal();
  const { openEstimate } = useEstimate();
  const [openMat, setOpenMat] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [storageOpen, setStorageOpen] = useState(null);

  return (
    <>
      <Seo title="Kitchen Renovations Edmonton" path="/services/kitchen"
        description="The complete guide to kitchen renovations in Edmonton — styles, cabinets, countertops, storage, lighting, layouts, flooring and costs. Get a free estimate from Classic Interior Renovations."
        image={IMG.hero} />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Modern luxury kitchen renovation in Edmonton" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.26em] text-xs font-medium uppercase">Kitchen Renovations</span>
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 max-w-3xl">
            <span className="font-light">Your Dream</span> <span className="font-bold text-[#2f9fe6]">Kitchen</span>, Built to Last
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-9">
            From layout and cabinetry to countertops and lighting — the complete guide to renovating your Edmonton kitchen, and the team to build it.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Get a Free Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/projects" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">View Our Work</Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Why Renovate" title="Why renovate your kitchen?"
            sub="The kitchen is the heart of the home — and the most impactful space to renovate. A thoughtful remodel improves how you cook, gather and live, while adding lasting value to your property. Whether you want more storage, better light or a fresh modern look, the right renovation transforms your everyday." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <div key={b.title} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-8 border border-[#eef1f4] hover:shadow-xl hover:-translate-y-1 transition-all`}>
                  <div className="w-14 h-14 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-5"><Icon className="w-7 h-7 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{b.title}</h3>
                  <p className="text-[#6b7683] text-sm leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-[#0f2740] text-white overflow-hidden">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Our Process</span><span className="h-px w-10 bg-[#2f9fe6]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Our kitchen renovation process</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <div key={p.title} className={`reveal delay-${(i % 5) + 1} relative`}>
                  <div className="w-14 h-14 rounded-full bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <div className="text-[#2f9fe6] font-display font-bold text-sm mb-1">STEP {i + 1}</div>
                  <h3 className="font-display text-lg font-semibold mb-2">{p.title}</h3>
                  <p className="text-[#a9c6dd] text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STYLES */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Kitchen Styles" title="Choose your style" sub="Hover to explore the looks we design and build." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {STYLES.map((s, i) => (
              <div key={s.name} className={`reveal delay-${(i % 3) + 1} relative group shape-soft h-80 cursor-pointer`}>
                <img src={s.img} alt={`${s.name} kitchen`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/90 via-[#0f2740]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-display text-2xl font-bold text-white mb-2">{s.name}</h3>
                  <p className="text-white/80 text-sm max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CABINET GUIDE */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative reveal-left">
            <div className="shape-arch-top"><img src={IMG.cab2} alt="Kitchen cabinetry" className="w-full h-[520px] object-cover" /></div>
          </div>
          <div className="reveal-right">
            <Heading eyebrow="Cabinet Guide" title="Cabinet materials" />
            <div className="divide-y divide-[#dcd3c3] border-t border-[#dcd3c3]">
              {CABINET_MATERIALS.map((m, i) => (
                <div key={m.name}>
                  <button onClick={() => setOpenMat(openMat === i ? -1 : i)} className="w-full flex items-center justify-between py-4 text-left group">
                    <span className={`font-display text-lg font-semibold ${openMat === i ? "text-[#2f9fe6]" : "text-[#0f2740] group-hover:text-[#2f9fe6]"}`}>{m.name}</span>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center ${openMat === i ? "bg-[#2f9fe6] text-white" : "bg-white text-[#0f2740]"}`}>{openMat === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-400 ${openMat === i ? "max-h-40 pb-5" : "max-h-0"}`}><p className="text-[#5a6570] leading-relaxed">{m.text}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SMART STORAGE */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Storage" title="Smart storage solutions" sub="Make every inch work harder with clever, custom storage. Click any option to explore variations." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {STORAGE.map((s, i) => (
              <button
                key={s.title}
                type="button"
                onClick={() => setStorageOpen(i)}
                data-testid={`storage-card-${i}`}
                className={`reveal delay-${(i % 5) + 1} group text-left focus:outline-none`}
              >
                <div className="shape-soft mb-3 relative">
                  <img src={s.variants[0]} alt={s.title} loading="lazy" className="w-full h-36 object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-[#0f2740]/0 group-hover:bg-[#0f2740]/40 transition-colors duration-300 flex items-center justify-center">
                    <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <span className="absolute bottom-2 right-2 bg-white/90 text-[#0f2740] text-[10px] font-semibold px-2 py-0.5 rounded-full">{s.variants.length} photos</span>
                </div>
                <div className="font-medium text-[#0f2740] text-sm group-hover:text-[#2f9fe6] transition-colors">{s.title}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SMART STORAGE LIGHTBOX CAROUSEL */}
      <Dialog open={storageOpen !== null} onOpenChange={(o) => !o && setStorageOpen(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white border-0" data-testid="storage-dialog">
          {storageOpen !== null && (
            <div className="p-6 sm:p-8">
              <DialogTitle className="font-display text-2xl font-bold text-[#0f2740] mb-1" data-testid="storage-dialog-title">
                {STORAGE[storageOpen].title}
              </DialogTitle>
              <p className="text-[#6b7683] text-sm mb-6">Swipe through design variations for this storage solution.</p>
              <Carousel className="w-full" opts={{ loop: true }}>
                <CarouselContent>
                  {STORAGE[storageOpen].variants.map((img, vi) => (
                    <CarouselItem key={vi}>
                      <div className="rounded-2xl overflow-hidden">
                        <img src={img} alt={`${STORAGE[storageOpen].title} ${vi + 1}`} className="w-full h-[420px] object-cover" data-testid={`storage-variant-${vi}`} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-3" data-testid="storage-carousel-prev" />
                <CarouselNext className="right-3" data-testid="storage-carousel-next" />
              </Carousel>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* COUNTERTOPS */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Countertops" title="Countertop materials" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {COUNTERTOPS.map((c, i) => (
              <div key={c.name} className={`reveal delay-${(i % 4) + 1} bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl transition-all`}>
                <img src={c.img} alt={`${c.name} countertop`} loading="lazy" className="w-full h-40 object-cover" />
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-4">{c.name}</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Pros:</b> {c.pros}</span></li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Maintenance:</b> {c.maintenance}</span></li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Durability:</b> {c.durability}</span></li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-[#2f9fe6] shrink-0 mt-0.5" /><span className="text-[#4a5560]"><b className="text-[#0f2740]">Best for:</b> {c.bestFor}</span></li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHTING */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Lighting Design" title="Layered kitchen lighting" sub="Great kitchens use three layers of light working together." />
          <div className="grid md:grid-cols-3 gap-8">
            {LIGHTING.map((l, i) => (
              <div key={l.name} className={`reveal delay-${(i % 3) + 1} group`}>
                <div className="shape-soft mb-5"><img src={l.img} alt={l.name} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-2">{l.name}</h3>
                <p className="text-[#6b7683] text-sm leading-relaxed">{l.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LAYOUTS */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Kitchen layouts</h2>
            <p className="text-[#a9c6dd] text-lg">Find the configuration that fits your space and how you cook.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {LAYOUTS.map((l, i) => (
              <div key={l.name} className={`reveal delay-${(i % 5) + 1} bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-[#2f9fe6] transition-colors`}>
                <div className="w-12 h-12 rounded-lg bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><LayoutGrid className="w-6 h-6 text-[#2f9fe6]" /></div>
                <h3 className="font-display text-lg font-semibold mb-2">{l.name}</h3>
                <p className="text-[#a9c6dd] text-sm">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOOR STYLES + HARDWARE + FLOORING */}
      <section className="py-24 bg-white">
        <div className="container-x space-y-16">
          <div>
            <Heading eyebrow="Details" title="Cabinet door styles" />
            <div className="flex flex-wrap gap-3">
              {DOOR_STYLES.map((d) => (<span key={d} className="px-6 py-3 rounded-full bg-[#f2f5f8] text-[#0f2740] font-semibold hover:bg-[#2f9fe6] hover:text-white transition-colors cursor-default">{d}</span>))}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="reveal-left">
              <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-5">Hardware options</h3>
              <div className="flex flex-wrap gap-3">
                {HARDWARE.map((h) => (<span key={h} className="px-5 py-2.5 rounded-full border border-[#dfe4e9] text-[#0f2740] font-medium hover:border-[#2f9fe6] hover:text-[#2f9fe6] transition-colors cursor-default">{h}</span>))}
              </div>
            </div>
            <div className="reveal-right">
              <h3 className="font-display text-2xl font-bold text-[#0f2740] mb-5">Kitchen flooring</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {FLOORING.map((f) => (
                  <div key={f.name} className="bg-[#f7f9fb] rounded-xl p-5 border border-[#eef1f4]">
                    <div className="font-display font-semibold text-[#0f2740] mb-1">{f.name}</div>
                    <p className="text-[#6b7683] text-sm">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM FEATURES */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x">
          <Heading center eyebrow="Premium" title="Popular kitchen features" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={f} className={`reveal delay-${(i % 3) + 1} flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-[#e5ddce]`}>
                <span className="w-7 h-7 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" /></span>
                <span className="font-medium text-[#0f2740]">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Transformation" title="Before & after" />
          <div className="max-w-3xl mx-auto reveal"><BeforeAfterSlider before={IMG.f3} after={IMG.m1} label="Kitchen transformation" /></div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Inspiration" title="Kitchen inspiration gallery" />
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <button key={i} onClick={() => setLightbox(g)} className="mb-4 block w-full shape-soft group">
                <img src={g} alt={`Kitchen inspiration ${i + 1}`} loading="lazy" className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i % 3 === 0 ? "h-72" : i % 3 === 1 ? "h-56" : "h-64"}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container-x max-w-3xl">
          <Heading center eyebrow="FAQ" title="Kitchen renovation FAQ" />
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-[#e7e9ec] rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left">
                  <span className={`font-display font-semibold ${openFaq === i ? "text-[#2f9fe6]" : "text-[#0f2740]"}`}>{f.q}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${openFaq === i ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740]"}`}>{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-60" : "max-h-0"}`}><p className="px-6 pb-5 text-[#6b7683] leading-relaxed">{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />

      {/* RESOURCES */}
      <section className="py-20 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="mb-10 reveal">
            <div className="flex items-center gap-3 mb-3"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Resources</span></div>
            <h2 className="font-display text-3xl font-bold">Kitchen renovation resources</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RESOURCES.map((r, i) => (
              <Link key={r} to="/contact" className={`reveal delay-${(i % 3) + 1} flex items-center justify-between gap-3 bg-white/5 rounded-xl px-5 py-4 border border-white/10 hover:border-[#2f9fe6] hover:bg-white/10 transition-all group`}>
                <span className="text-[#dbe7f1] group-hover:text-white font-medium">{r}</span>
                <ArrowRight className="w-4 h-4 text-[#2f9fe6] group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Kitchen" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </>
  );
}
